/**
 * The application shell: one global top navigation bar.
 *
 * The worksheet builder owns its question outline, so application navigation must
 * not compete with it as a second left rail. Every role-based destination remains
 * available here; narrow screens expose the same links from a compact menu.
 */

import { useEffect, useRef } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Icon } from '../components/icons'
import { BrandMark } from '../components/BrandMark'
import { Picture } from '../components/Picture'
import { api } from '../lib/api/client'
import { useAuth } from './useAuth'
import './shell.css'

/**
 * Native `<details>` gives a disclosure that opens; it does not give one that
 * closes when you look elsewhere. Both header menus float over the page, so
 * without this the account panel could only be dismissed by hitting its own
 * summary again - and covered whatever was underneath until you did.
 *
 * The rules are the ones `Popover` in components/ui.tsx already follows, so the
 * app's two dropdown mechanisms behave the same way: click outside, press
 * Escape, or move to another route.
 */
function useDismissableMenus() {
  const root = useRef<HTMLElement>(null)
  const { pathname } = useLocation()

  const closeAll = (except?: Element | null) => {
    root.current?.querySelectorAll('details[open]').forEach((menu) => {
      if (menu !== except) menu.removeAttribute('open')
    })
  }

  useEffect(() => {
    const away = (event: MouseEvent) => {
      const open = root.current?.querySelectorAll('details[open]')
      if (!open?.length) return
      const inside = (event.target as Node | null)?.nodeType
        ? [...open].some((menu) => menu.contains(event.target as Node))
        : false
      if (!inside) closeAll()
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const open = root.current?.querySelector<HTMLElement>('details[open]')
      if (!open) return
      closeAll()
      // Focus goes back to the control that opened the menu, rather than being
      // dropped at the top of the document.
      open.querySelector<HTMLElement>('summary')?.focus()
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', escape)
    }
  }, [])

  // A menu left open across a navigation would hang over the new screen.
  useEffect(() => closeAll(), [pathname])

  return { root, closeAll }
}

type Item = { to: string; label: string; icon: keyof typeof Icon; when?: boolean }
type NavGroup = { label?: string; items: Item[] }

export function Shell() {
  const { user, signOut, has } = useAuth()
  const { root: headerRef, closeAll } = useDismissableMenus()
  const isSiteAdmin = has('admin')
  const school = useQuery({ queryKey: ['school'], queryFn: () => api.school() })
  const schoolName = school.data?.name ?? user?.school_name ?? 'School'
  const schoolBranch = school.data?.branch ?? user?.school_branch
  const currentRoles = roleLabels(user?.roles ?? [])

  // The site administrator runs the platform, not a classroom. These four are
  // deliberately *not* offered to them:
  //
  //   Papers   listed every paper on every school, under the heading "My papers"
  //   Review   every paper any school had sent to any reviewer
  //   Printing every approved paper on the platform, ready to download
  //   Pictures the platform school's own library, which no teacher ever fills
  //
  // None of that is part of onboarding a school, handing it over, or settling
  // its AI bill - and the first three quietly made the operator a reader of
  // every school's unpublished exam papers. Support can still open a specific
  // paper from a direct link; nothing is browsable by default.
  const groups: NavGroup[] = [
    {
      items: [
        { to: '/home', label: 'Home', icon: 'home' },
        {
          to: '/audit',
          label: 'Audit',
          icon: 'audit',
          when: isSiteAdmin || has('school_admin'),
        },
        { to: '/papers', label: 'Papers', icon: 'papers', when: !isSiteAdmin && has('teacher') },
        {
          to: '/pictures',
          label: 'Pictures',
          icon: 'pictures',
          when: !isSiteAdmin && has('teacher'),
        },
        { to: '/review', label: 'Review', icon: 'review', when: !isSiteAdmin && has('reviewer') },
        {
          to: '/printing',
          label: 'Printing',
          icon: 'printing',
          when: !isSiteAdmin && has('print_admin', 'school_admin'),
        },
      ],
    },
    {
      label: 'Platform',
      items: [
        { to: '/schools', label: 'Schools', icon: 'schools', when: isSiteAdmin },
        { to: '/site-settings', label: 'Site settings', icon: 'settings', when: isSiteAdmin },
      ],
    },
    {
      label: 'Administration',
      items: [
        {
          to: '/settings',
          label: 'School settings',
          icon: 'settings',
          when: !isSiteAdmin && has('school_admin'),
        },
      ],
    },
  ]
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.when !== false),
    }))
    .filter((group) => group.items.length)

  return (
    <div className="tc-shell">
      <a className="tc-skip" href="#main">
        Skip to content
      </a>

      <header className="tc-global-header" ref={headerRef}>
        <a className="tc-global-brand" href="/" target="_blank" rel="noopener noreferrer" title="TeachCraftAI">
          <BrandMark size={26} />
          <span className="tc-wordmark">
            TeachCraft<span className="tc-brand-ai">AI</span>
          </span>
        </a>

        <nav className="tc-global-nav" aria-label="Main">
          {visibleGroups.map((group, groupIndex) => (
            <div className="tc-global-nav-group" key={group.label ?? `main-${groupIndex}`}>
              {group.items.map((item) => {
                const Glyph = Icon[item.icon]
                return (
                  <NavLink key={item.to} to={item.to} className="tc-global-nav-item">
                    <Glyph size={17} />
                    <span>{item.label}</span>
                  </NavLink>
                )
              })}
            </div>
          ))}
        </nav>

        <details
          className="tc-global-menu"
          onToggle={(event) => event.currentTarget.open && closeAll(event.currentTarget)}
        >
          <summary aria-label="Open main menu">
            <Icon.menu size={18} />
            <span>Menu</span>
          </summary>
          <nav className="tc-global-menu-panel" aria-label="Main menu">
            {visibleGroups.map((group, groupIndex) => (
              <div className="tc-global-menu-group" key={group.label ?? `mobile-${groupIndex}`}>
                {group.label && <span className="tc-global-menu-label">{group.label}</span>}
                {group.items.map((item) => {
                  const Glyph = Icon[item.icon]
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className="tc-global-menu-item"
                      onClick={() => closeAll()}
                    >
                      <Glyph size={17} />
                      <span>{item.label}</span>
                    </NavLink>
                  )
                })}
              </div>
            ))}
          </nav>
        </details>

        <div className="tc-global-context">
          <span
            className="tc-global-school"
            aria-label={`School: ${schoolName}${schoolBranch ? `, ${schoolBranch}` : ''}`}
          >
            {school.data?.logo_image_id ? (
              <Picture id={school.data.logo_image_id} className="tc-global-school-logo" />
            ) : (
              <span className="tc-global-school-logo tc-global-school-logo-fallback" aria-hidden="true">
                <Icon.schools size={17} />
              </span>
            )}
            <span className="tc-global-school-copy">
              <span className="tc-global-school-name">{schoolName}</span>
              {schoolBranch && <span className="tc-global-branch">{schoolBranch}</span>}
            </span>
          </span>

          {user && (
            <details
              className="tc-account-menu"
              onToggle={(event) => event.currentTarget.open && closeAll(event.currentTarget)}
            >
              <summary aria-label={`Signed in as ${user.full_name}, ${currentRoles}, ${user.email}`}>
                <span className="tc-account-avatar" aria-hidden="true">
                  {initials(user.full_name)}
                </span>
                <span className="tc-account-copy">
                  <span className="tc-account-name">{user.full_name}</span>
                  <span className="tc-account-role">Role · {compactRoleLabels(user.roles)}</span>
                </span>
                <span className="tc-account-chevron" aria-hidden="true">
                  <Icon.chevronDown size={14} />
                </span>
              </summary>
              <div className="tc-account-panel">
                <strong>{user.full_name}</strong>
                <span className="tc-account-panel-role">Role · {currentRoles}</span>
                <span>{user.email}</span>
                <span>{user.school_name}</span>
                <button type="button" onClick={signOut}>
                  <Icon.signout size={16} />
                  Sign out
                </button>
              </div>
            </details>
          )}
        </div>
      </header>

      <main id="main" className="tc-main">
        <Outlet />
      </main>
    </div>
  )
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Site administrator',
  school_admin: 'School administrator',
  teacher: 'Teacher',
  reviewer: 'Reviewer',
  print_admin: 'Print administrator',
}

function roleLabels(roles: string[]) {
  const priority = ['admin', 'school_admin', 'teacher', 'reviewer', 'print_admin']
  const ordered = [
    ...priority.filter((candidate) => roles.includes(candidate)),
    ...roles.filter((role) => !priority.includes(role)),
  ]
  return ordered.length
    ? ordered.map((role) => ROLE_LABELS[role] ?? role.replaceAll('_', ' ')).join(' · ')
    : 'User'
}

function compactRoleLabels(roles: string[]) {
  const full = roleLabels(roles).split(' · ')
  return full.length > 1 ? `${full[0]} +${full.length - 1}` : full[0]
}
